import React from 'react'
import { Badge, Divider, MenuList, Typography } from '@material-ui/core'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import { createStyles, Theme, makeStyles, withStyles } from '@material-ui/core/styles'
import ViewQuiltIcon from '@material-ui/icons/ViewQuilt'
import ChatIcon from '@material-ui/icons/Chat'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import PeopleIcon from '@material-ui/icons/People'
import ShowChartIcon from '@material-ui/icons/ShowChart'
import MuiMenuItem from '@material-ui/core/MenuItem'
import BuildIcon from '@material-ui/icons/Build'

import palette from '../../../../themes/palette'
import { withRouter } from 'react-router'
import { flow } from 'lodash'
import { connectAdminThemeV2 } from '../../../../connectTheme'
import { inject, observer } from 'mobx-react'
import { IStore } from '../../../../store/models'
import { IUser } from '../../../../store/models/User'
import Icon from '@material-ui/core/Icon'
interface Props {
  user?: IUser
  history: any
  active?: string
  showActive?: boolean
  match?: { params?: { page?: string } }
  onClose?()
}

/**
 * One entry in the admin sidebar.
 *
 * The array below is an object-literal list whose members have never had the
 * same shape -- Dashboard carries `url` and no `id`, the role-gated ones carry
 * neither -- and `isNew` now makes that worse by existing on exactly one entry.
 *
 * TypeScript does infer this correctly on its own today: for an array of object
 * literals it normalises the element type, so absent keys come back as
 * `undefined` rather than being missing, and destructuring `isNew` compiles
 * unannotated. Named anyway, because that inference is a convenience, not a
 * contract -- it says nothing about which keys are *meant* to be optional, and
 * the next person adding an entry gets no signal from it at all.
 */
type AdminMenuEntry = {
  label: string
  name?: string
  url?: string
  MenuIcon: React.ElementType
  id?: string
  /** Temporary -- see the badge note in the render below. */
  isNew?: boolean
  /**
   * Draw a hairline immediately above this entry. A divider has no label, no
   * route and no id, so it cannot be an entry of its own in a list rendered by
   * a single map; it hangs off the entry it precedes instead. One mechanism --
   * nothing in the render below looks at labels to decide where a rule goes.
   */
  dividerBefore?: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerContainer: {
      overflow: 'auto',
      paddingTop: theme.spacing(2)
    },
    menuListItem: {
      paddingTop: theme.spacing(1.7),
      paddingBottom: theme.spacing(1.7)
    },
    // Sits clear of the label rather than on top of it -- Badge's default
    // position overlaps the last character.
    //
    // Coloured here rather than by a `color` prop on the Badge. The theme's
    // `secondary.main` is palette.teal, which reads as a foreign green in a
    // sidebar whose only accent is yellow -- the selected MenuItem below pairs
    // palette.lightYellow with palette.black. This uses palette.yellow, a stop
    // stronger than that background, so the badge still catches the eye on a
    // row that is itself highlighted.
    //
    // Both colours are set explicitly and neither is optional. Dropping the
    // `color` prop also drops MUI's `contrastText`, which was the badge's only
    // source of a foreground colour; and white on yellow is about 1.8:1, which
    // fails WCAG outright. Black on palette.yellow is about 12.4:1.
    newBadge: {
      right: theme.spacing(-3),
      top: theme.spacing(0.5),
      height: 16,
      minWidth: 16,
      fontSize: 10,
      padding: theme.spacing(0, 0.5),
      backgroundColor: palette.yellow,
      color: palette.black
    },
    // The seam between day-to-day admin above and developer tooling below.
    // Deliberately quiet: lightestgrey rather than MUI's default divider
    // colour, and inset from the drawer edges, so it reads as a grouping
    // rather than as a rule across the sidebar.
    groupDivider: {
      backgroundColor: palette.lightestgrey,
      margin: theme.spacing(1, 2)
    }
  })
)

const MenuItem = withStyles({
  root: {
    '&$selected': {
      color: palette.black,
      backgroundColor: palette.lightYellow
    },
    '&$selected:hover': {
      color: palette.black,
      backgroundColor: palette.lightYellow
    }
  },
  selected: {}
})(MuiMenuItem)

const AdminMenu = ({ user, history, active, onClose, match, showActive }: Props) => {
  const classes = useStyles()

  // The "Need help? / Check our documentation" card that used to live here was
  // removed. It linked to https://help.inclusivv.co/en/collections/2743414-... --
  // Inclusivv's Intercom help centre, which no longer exists (every URL 404s),
  // and OOH has no replacement site. Its only call site was already commented
  // out, so nothing rendered it; this deletes the dead component along with the
  // dead URL rather than leaving a link nobody can fix.

  return (
    <>
      <div className={classes.drawerContainer}>
        <MenuList>
          {([
            { label: 'Dashboard', name: undefined, url: '/admin', MenuIcon: ViewQuiltIcon },
            { label: 'Topics', name: 'topics', MenuIcon: ChatIcon, id: 'ic-admin-nav-topics' },
            { label: 'Conversations', name: 'conversations', MenuIcon: CalendarTodayIcon, id: 'ic-admin-nav-conversations' },
            { label: 'Guests/Hosts', name: 'attendees', MenuIcon: PeopleIcon, id: 'ic-admin-nav-attendees' },
            ...(user?.isPartnerAdmin() ? [{ label: 'Stories', name: 'stories', MenuIcon: StoriesIcon }] : []),
            { label: 'Analytics', name: 'analytics', MenuIcon: ShowChartIcon, id: 'ic-admin-nav-analytics' },
            ...(user?.isPartnerAdmin() ? [{ label: 'Feedback', name: 'nps_answers', MenuIcon: NpsAnswersIcon }] : []),
            ...(user?.isSuperAdmin() ? [{ label: 'Partners', name: 'settings', MenuIcon: PartnersIcon, id: 'ic-admin-nav-partners' }] : []),
            // Everything below the divider is tooling rather than day-to-day
            // administration: nobody running a conversation programme needs it,
            // and grouping it apart keeps Partners -- which they do use -- next
            // to the rest of their work instead of stranded under two links
            // that are not for them.
            //
            // Nav-gated to super admins, matching the /admin/settings
            // ("Partners") entry above. This is deliberately NOT a security
            // control and the route is left open: the embeds and the
            // /api/embed/* feeds behind them are public by design, so there is
            // nothing here to protect. It only decides who is shown the link to
            // the snippet generator, and a community admin who types
            // /admin/embeds still reaching it is an accepted outcome.
            ...(user?.isSuperAdmin()
              ? [{ label: 'Embeds', name: 'embeds', MenuIcon: EmbedsIcon, id: 'ic-admin-nav-embeds', isNew: true, dividerBefore: true }]
              : []),
            // Developer-facing tools -- currently the email test bench. Gated
            // to super admins like Embeds above, and for a stronger reason:
            // the page behind this link can send platform email. The gate that
            // enforces that is EmailTestBenchPolicy on the server; this only
            // decides who is shown the link.
            //
            // Reuses the isNew badge in the render below rather than adding a
            // second badge mechanism. When Embeds' badge comes off, this one
            // goes with it and the Badge wrapper disappears for both.
            ...(user?.isSuperAdmin()
              ? [{ label: 'Dev', name: 'dev', MenuIcon: DevIcon, id: 'ic-admin-nav-dev', isNew: true }]
              : [])
          ] as AdminMenuEntry[]).map(({ label, name, url, MenuIcon, id, isNew, dividerBefore }) => [
            // An array rather than a fragment: MenuList walks its children by
            // index to decide which one takes focus, and warns by name on a
            // Fragment child. React flattens this, and the <hr> the Divider
            // renders carries no tabindex, so arrow-key navigation steps over
            // it rather than stalling on it.
            dividerBefore && <Divider key={`${label}-divider`} className={classes.groupDivider} />,
            <MenuItem
              key={label}
              {...(showActive !== false ? { selected: active === name || name === match?.params?.page } : {})}
              className={classes.menuListItem}
              id={id}
              onClick={() => {
                history.push(url !== undefined ? url : `/admin/${name}`)
                onClose && onClose()
              }}
            >
              <ListItemIcon>
                <MenuIcon />
              </ListItemIcon>
              {/*
                TEMPORARY. The "New" badge is here to draw super admins to a
                page they have never seen; it should come out once the first
                customer embed is live and the entries are no longer news.
                Remove `isNew` from the entries above and this wrapper with it.

                The Badge wraps the Typography rather than sitting inside it,
                and that is load-bearing: `noWrap` sets `overflow: hidden`, and
                the badge is positioned outside the label box, so as a child it
                was clipped away and never rendered on screen at all. No offset
                fixes that -- one that happens to fit inside the clip box is a
                longer label away from vanishing again. Out here there is no
                clipping ancestor.

                `badgeContent={isNew ? 'New' : null}` rather than the
                `invisible` prop: a null badgeContent makes MUI treat the badge
                as invisible anyway, and leaves the span empty, so the word
                "New" is not sitting in the DOM -- and the accessibility tree --
                of every other nav entry.
              */}
              <Badge classes={{ badge: classes.newBadge }} badgeContent={isNew ? 'New' : null}>
                <Typography variant='inherit' noWrap>
                  {label}
                </Typography>
              </Badge>
            </MenuItem>
          ])}
        </MenuList>
      </div>
    </>
  )
}

const StoriesIcon = (props) => {
  return <Icon className='far fa-book-spells' />
}

const NpsAnswersIcon = (props) => {
  return <Icon className='fal fa-poll-people' />
}

const EmbedsIcon = () => {
  return <Icon className='far fa-window-restore' />
}

// A MUI icon rather than the FontAwesome <Icon> its neighbours use: the
// FontAwesome set here is the "far"/"fal" families, which have no wrench or
// spanner, and @material-ui/icons is already a dependency of this file's
// imports.
const DevIcon = () => {
  return <BuildIcon />
}

export const PartnersIcon = (props) => {
  return <Icon className='fal fa-folder' />
}
export default flow(
  observer,
  inject<FIXME, FIXME>(
    ({
      mst: {
        auth: { user }
      }
    }: {
      mst: IStore
    }) => {
      return {
        user
      }
    }
  ),
  connectAdminThemeV2,
  withRouter
)(AdminMenu)
